import {
  subscribeToDailyActivity,
  type DailyActivityState,
} from './dailyRewards';

type DashboardDailySnapshot = {
  uid: string;
  dateKey: string;
  activity: DailyActivityState | null;
  ready: boolean;
};

type Listener = () => void;

let snapshot: DashboardDailySnapshot = {
  uid: '',
  dateKey: '',
  activity: null,
  ready: false,
};

let unsubscribe: (() => void) | null = null;
const listeners = new Set<Listener>();

const storageKeyFor = (uid: string, dateKey: string) =>
  `roddo:dashboard-daily:${uid}:${dateKey}`;

function emit() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {}
  });
}

function readCached(uid: string, dateKey: string): DailyActivityState | null {
  if (typeof window === 'undefined' || !uid || !dateKey) return null;
  try {
    const raw = window.sessionStorage.getItem(storageKeyFor(uid, dateKey));
    return raw ? (JSON.parse(raw) as DailyActivityState) : null;
  } catch {
    return null;
  }
}

function writeCached(uid: string, dateKey: string, activity: DailyActivityState) {
  if (typeof window === 'undefined' || !uid || !dateKey) return;
  try {
    window.sessionStorage.setItem(storageKeyFor(uid, dateKey), JSON.stringify(activity));
  } catch {}
}

export function getDashboardDailySnapshot(uid: string, dateKey: string): DashboardDailySnapshot {
  const cleanUid = String(uid || '').trim();
  const cleanDateKey = String(dateKey || '').trim();

  if (snapshot.uid === cleanUid && snapshot.dateKey === cleanDateKey) {
    return snapshot;
  }

  const cached = readCached(cleanUid, cleanDateKey);
  return {
    uid: cleanUid,
    dateKey: cleanDateKey,
    activity: cached,
    ready: Boolean(cached),
  };
}

export function startDashboardDailyStore(uid: string, dateKey: string) {
  const cleanUid = String(uid || '').trim();
  const cleanDateKey = String(dateKey || '').trim();

  if (!cleanUid || !cleanDateKey) {
    unsubscribe?.();
    unsubscribe = null;
    snapshot = { uid: '', dateKey: '', activity: null, ready: false };
    emit();
    return;
  }

  if (snapshot.uid === cleanUid && snapshot.dateKey === cleanDateKey && unsubscribe) {
    return;
  }

  unsubscribe?.();
  unsubscribe = null;

  const cached = readCached(cleanUid, cleanDateKey);
  snapshot = {
    uid: cleanUid,
    dateKey: cleanDateKey,
    activity: cached,
    ready: Boolean(cached),
  };
  emit();

  unsubscribe = subscribeToDailyActivity(
    cleanUid,
    cleanDateKey,
    (activity) => {
      snapshot = {
        uid: cleanUid,
        dateKey: cleanDateKey,
        activity,
        ready: true,
      };
      writeCached(cleanUid, cleanDateKey, activity);
      emit();
    },
    (error) => {
      console.warn('[DashboardDailyStore] daily activity unavailable', error);
      snapshot = { ...snapshot, ready: true };
      emit();
    },
  );
}

export function subscribeDashboardDailyStore(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
